<template>
  <ul class="navigation-controls">
    <li class="navigation-controls__link-wrapper">
      <BookmarkIndicator class="navigation-controls__link" />
    </li>
    <li class="navigation-controls__link-wrapper">
      <LinkButton
        id="search-button-for-nexus"
        ref="searchButton"
        class="navigation-controls__link"
        tag="button"
        type="button"
        variant="transparent"
        aria-label="Suchen"
        no-styling
        data-tealium-rel="navi-top.button.oeffne-suchformular"
        @click="loadSearch"
      >
        <SvgIcon
          class="btn__icon"
          :image="searchIcon"
          no-icon-class
        />
        <slot />
      </LinkButton>
    </li>
    <li class="navigation-controls__link-wrapper">
      <CartIndicator class="navigation-controls__link" />
    </li>
    <li class="navigation-controls__menu navigation-controls__link-wrapper">
      <LinkButton
        ref="menuToggle"
        tag="button"
        type="button"
        variant="transparent"
        class="navigation-controls__link"
        :aria-label="isOpen ? 'Menü schließen' : 'Menü öffnen'"
        :aria-expanded="isOpen ? 'true' : 'false'"
        aria-haspopup="dialog"
        aria-controls="primary-navigation"
        @click="toggleMobileNav"
      >
        <SvgIcon
          :image="menu"
          class="btn__icon navigation-controls__menu-open"
          no-icon-class
        />
        <SvgIcon
          :image="menuClose"
          class="btn__icon navigation-controls__menu-close"
          no-icon-class
        />
      </LinkButton>
    </li>
  </ul>
</template>

<script setup lang="ts">
import CartIndicator from '~/components/navigation/cart-indicator.vue';
import { LinkButton } from '@i22team/td-longtail-vue-components';
import menu from '~/assets/images/navigation/nav-menu.svg';
import menuClose from '~/assets/images/menu-close.svg';
import searchIcon from '~/assets/images/navigation/search.svg';

const SCRIPT_TAG_ID = 'searchScriptTag';

declare global {
  interface IceSearchConfig {
    basePath: string;
    triggerSelector: string;
  }

  interface IceSearch {
    open: () => void;
  }

  interface Window {
    IceSearchConfig?: IceSearchConfig;
    IceSearch?: IceSearch;
  }
}

const emit = defineEmits<{
  'toggle-mobile-nav': [isOpen: boolean];
}>();

const route = useRoute();

const isOpen = ref(false);

watch(route, () => {
  isOpen.value = false;
});

watch(isOpen, () => {
  emit('toggle-mobile-nav', isOpen.value);
});

onMounted(() => {
  if (import.meta.client && route.query?.q) {
    openSearch();
  }
});

const toggleMobileNav = () => {
  isOpen.value = !isOpen.value;
};

const loadSearch = () => {
  window.IceSearchConfig = {
    basePath: '/search/',
    triggerSelector: 'button#search-button-for-nexus',
  };

  isOpen.value = false;
  if (!document.getElementById(SCRIPT_TAG_ID)) {
    const search = document.createElement('script');
    search.setAttribute('src', '/search/specSearch-egsolo.min.js' as string);
    search.setAttribute('id', SCRIPT_TAG_ID);
    search.addEventListener('load', () => {
      setTimeout(() => {
        window.IceSearch?.open();
      }, 1);
    });
    document.head.append(search);
  } else {
    window.IceSearch?.open();
  }
};

const openSearch = () => {
  loadSearch();
  window?.scrollTo({ top: 0, left: 0 });
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.navigation-controls {
  display: flex;
  align-items: center;
  height: 100%;
  list-style: none;
  gap: $grid-base;

  @include mq($mq-sm) {
    gap: 1.5 * $grid-base;
  }

  &__link-wrapper {
    height: 100%;
    position: relative;

    @include mq($mq-lg) {
      height: 70px;
    }
  }

  &__link {
    --btn-radius: 0;
    --btn-padding: 0 6px;
    --btn-color-background-custom-hover: transparent;
    --hover-grow-width: 0;
    --focus-offset-large: -2px;
    --btn-focus-shadow: none;
    --btn-height: 100%;
    transition: color 0.125s ease-out;
    width: 34px;

    --btn-icon-width: 100%;
    --btn-icon-height: auto;
    @include mq($mq-sm) {
      width: 36px;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      height: 4px;
      width: 100%;
      background-color: transparent;
      transition: background-color 0.125s ease-out;
    }

    &--active,
    &:hover {
      --btn-color: var(--color-primary);

      &::after {
        background-color: var(--color-primary);
      }
    }
  }

  &__menu {
    @include mq($mq-lg) {
      display: none;
    }
  }

  &__menu-close {
    --btn-icon-width: 18px;
  }

  .cart-indicator {
    .navigation-controls__item-name {
      bottom: 3px;
    }
  }
}

body:not(.mobile-nav-open) {
  .navigation-controls__menu-close {
    display: none;
  }
}

body.mobile-nav-open {
  .navigation-controls__menu-open {
    display: none;
  }
}
</style>
